import React, { useEffect, useState } from 'react';
import { Modal, Tab, Tabs, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faClock, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const CallHistoryModal = ({ show, onHide, leadId }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (show && leadId) {
      loadCallHistory();
    }
  }, [show, leadId]);

  const loadCallHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `/api/leads/${leadId}/communication-history?type=call`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setCalls(response.data.communications || []);
    } catch (err) {
      setError('Failed to load call history');
      console.error('Error loading call history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faPhone} className="me-2" />
          Call History
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}
        
        {error && (
          <Alert variant="danger">{error}</Alert>
        )}
        
        {!loading && !error && calls.length === 0 && (
          <Alert variant="info">No call history available</Alert>
        )}
        
        {!loading && !error && calls.length > 0 && (
          <div>
            {calls.map((call, index) => (
              <div key={call.id || index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <span className={`badge ${call.direction === 'inbound' ? 'bg-success' : 'bg-primary'} me-2`}>
                      {call.direction === 'inbound' ? 'Incoming' : 'Outgoing'}
                    </span>
                    <span className="text-muted">
                      {formatDate(call.created_at)}
                    </span>
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {formatDuration(call.duration_seconds)}
                  </div>
                </div>
                
                <Tabs
                  activeKey={activeTab}
                  onSelect={setActiveTab}
                  className="mb-2"
                >
                  <Tab eventKey="summary" title="Summary">
                    <div className="p-2">
                      {call.transcript_summary ? (
                        <p>{call.transcript_summary}</p>
                      ) : (
                        <p className="text-muted">No summary available</p>
                      )}
                      
                      {call.sentiment_score && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Sentiment Score: 
                            <span className={`ms-1 badge ${
                              call.sentiment_score > 0.7 ? 'bg-success' : 
                              call.sentiment_score > 0.4 ? 'bg-warning' : 'bg-danger'
                            }`}>
                              {(call.sentiment_score * 100).toFixed(0)}%
                            </span>
                          </small>
                        </div>
                      )}
                    </div>
                  </Tab>
                  
                  <Tab eventKey="transcript" title="Transcript">
                    <div className="p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {call.transcript ? (
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.9rem' }}>
                          {call.transcript}
                        </pre>
                      ) : (
                        <p className="text-muted">No transcript available</p>
                      )}
                    </div>
                  </Tab>
                  
                  {call.ai_insights && (
                    <Tab eventKey="insights" title="AI Insights">
                      <div className="p-2">
                        {call.ai_insights.searchCriteria && (
                          <div className="mb-2">
                            <strong>Search Criteria:</strong>
                            <pre className="bg-light p-2 rounded" style={{ fontSize: '0.85rem' }}>
                              {JSON.stringify(call.ai_insights.searchCriteria, null, 2)}
                            </pre>
                          </div>
                        )}
                        {call.ai_insights.propertiesFound !== undefined && (
                          <p>
                            <strong>Properties Found:</strong> {call.ai_insights.propertiesFound}
                          </p>
                        )}
                        {call.ai_insights.contactCaptured !== undefined && (
                          <p>
                            <strong>Contact Captured:</strong> {call.ai_insights.contactCaptured ? 'Yes' : 'No'}
                          </p>
                        )}
                      </div>
                    </Tab>
                  )}
                </Tabs>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CallHistoryModal;